import { noteSocketClose } from "../../../../lib/access";
import { useCallback, useEffect, useRef, useState } from "react";
import { wsUrl } from "../../../../lib/wsUrl";

/**
 * The browser panel's WebSocket (bots B2, 2026-09-03) — the same lifecycle
 * shape as the terminal's: open once per panel, survive re-renders, CLOSE on
 * unmount (the server stops producing frames when the last viewer leaves).
 *
 * Server frames: ready / frame / screen / notice / closed / error. Client
 * frames: watch (once), take_over, resume, stop, input (only while taken
 * over or during a handoff — the server refuses otherwise and says so in a
 * notice), handoff_done, handoff_cancel (bots B3).
 */

export type BrowserSocketStatus =
	| { state: "connecting" }
	| { state: "ready" }
	| { state: "closed" }
	| { state: "error"; detail: string; code: string };

export interface ScreenInfo {
	id: string;
	agent_key: string;
	status: string;
	url: string;
	title: string;
	viewers?: number;
	blocked_requests?: number;
	held_submits?: number;
	/** Bots B3: the step the page asked the user for, while status is "handoff". */
	handoff?: { id: string; kind: string; host: string; url: string; status: string; message: string } | null;
}

export function useBrowserSocket(screenId: string) {
	const [status, setStatus] = useState<BrowserSocketStatus>({ state: "connecting" });
	const [frame, setFrame] = useState<string>("");
	const [screen, setScreen] = useState<ScreenInfo | null>(null);
	const [notice, setNotice] = useState<{ code: string; detail: string } | null>(null);
	const wsRef = useRef<WebSocket | null>(null);

	useEffect(() => {
		if (!screenId) return;
		const ws = new WebSocket(wsUrl("/ws/browser"));
		wsRef.current = ws;
		ws.onopen = () => ws.send(JSON.stringify({ type: "watch", screen: screenId }));
		ws.onmessage = (ev) => {
			let msg: Record<string, unknown>;
			try {
				msg = JSON.parse(ev.data as string);
			} catch {
				return;
			}
			switch (msg.type) {
				case "ready":
					setStatus({ state: "ready" });
					setScreen(msg.screen as ScreenInfo);
					break;
				case "frame":
					setFrame(String(msg.data ?? ""));
					setScreen((cur) => ({
						...(cur ?? { id: screenId, agent_key: "", status: "", url: "", title: "" }),
						url: String(msg.url ?? cur?.url ?? ""),
						title: String(msg.title ?? cur?.title ?? ""),
						status: String(msg.status ?? cur?.status ?? ""),
					}));
					break;
				case "screen":
					setScreen(msg.screen as ScreenInfo);
					break;
				case "notice":
					setNotice({ code: String(msg.code ?? ""), detail: String(msg.detail ?? "") });
					break;
				case "closed":
					setStatus({ state: "closed" });
					break;
				case "error":
					// Refusals are ACTIONABLE (a contract denial names the line to add).
					setStatus({ state: "error", detail: String(msg.detail ?? "The screen failed."), code: String(msg.code ?? "unknown") });
					break;
			}
		};
		ws.onerror = () => {
			setStatus((cur) => (cur.state === "ready" ? cur : { state: "error", detail: "Could not reach the browser service.", code: "socket" }));
		};
		ws.onclose = (ev) => {
			noteSocketClose(ev?.code);
			setStatus((cur) => (cur.state === "error" ? cur : cur.state === "closed" ? cur : { state: "closed" }));
		};
		return () => {
			ws.close();
			wsRef.current = null;
		};
	}, [screenId]);

	const send = useCallback((msg: Record<string, unknown>) => {
		const ws = wsRef.current;
		if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
	}, []);

	return { status, frame, screen, notice, send, clearNotice: () => setNotice(null) };
}
